import { useState, useEffect, useContext, useRef } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import ReadMore from "components/UpStyled/ReadMore";

import CategoryContext from "context/category-context";
import { AccountContext } from "context/account";
import StyleCard3 from "components/UpStyled/StyleCard3";
import ProductDetailModal from "pages/de/portfolio/components/ProductDetailModal";
import Comments from "pages/comment";

import api from "services/api";

import toast from "react-hot-toast";
import moment from "moment";
import Error404 from "pages/error/404";
import { compare } from "utils/helper";

const MAX_STYLE_NUMBER_COUNT = 12;
const MAX_TAG_LENGTH = 40;

const CollectionDetail = () => {
  const [detail, setDetail] = useState({});
  const [isNotFound, setIsNotFound] = useState(false);
  const [productDetailVisible, setProductDetailVisible] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  const params = useParams();
  const handle = params?.handle;
  const cid = params?.cid;

  const navigate = useNavigate();
  const account = useContext(AccountContext);

  const primaryImage = detail?.moodboard?.[0]?.img || {};
  const products = detail?.products || [];
  const metaFilesKey = ["imageFiles", "techPacksFiles", "referenceFiles"];

  const {
    tree,
    actions: { findNodeByID, findParents },
  } = useContext(CategoryContext);

  useEffect(() => {
    retrieveCollectionDetail();
  }, [cid]);

  const retrieveCollectionDetail = async () => {
    if (!cid) {
      return toast.error("No collection has been found!");
    }
    try {
      const { data } = await api.collections.retrieve(cid);
      const collection = data["collections"] ? data["collections"] : [];
      if (collection.length > 0) {
        setDetail(collection[0]);
        setIsNotFound(false);
      }
    } catch (err) {
      setIsNotFound(true);
      console.log(err);
      toast.error(`Retrieve collection detail failed`);
    }
  };

  const handleRefresh = () => {
    retrieveCollectionDetail();
  };

  const handleClickProduct = (product) => {
    setSelectedProductId(product?.id);
    setProductDetailVisible(true);
  };

  if (isNotFound) {
    return <Error404 />;
  }

  return (
    <div sx={{ p: 3 }}>
      {detail.id ? (
        <SuiBox>
          <SuiBox
            display="flex"
            alignItems="center"
            position="relative"
            minHeight={"28.75rem"}
            borderRadius="xl"
            sx={{
              backgroundImage: primaryImage?.url
                ? ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                    `linear-gradient(to top, #ffffff, transparent),url(${primaryImage?.url})`
                : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                    `${linearGradient("#ffffff", rgba(gradients.secondary.state, 0.4), 0)}`,
              backgroundSize: "cover",
              //backgroundPosition: "50%",
              backgroundPosition: "top",
              overflow: "hidden",
              cursor: "pointer",
            }}
          />

          <SuiBox sx={{ mt: "-10rem", mx: 3, py: 2, px: 2 }}>
            <Card sx={{ px: 2, py: 1 }}>
              <SuiBox sx={{ px: 2 }}>
                <SuiTypography variant="h5" fontWeight="bold">
                  {detail.title}
                </SuiTypography>
              </SuiBox>
              <SuiBox
                display="flex"
                justifyContent="flex-start"
                sx={{ minHeight: 250, overflowX: "auto" }}
              >
                {products.map((u, index) => {
                  return (
                    <SuiBox key={index}>
                      <StyleCard3
                        product={u}
                        size="md"
                        metaFilesKey={metaFilesKey}
                        handleSelectStyle={() => handleClickProduct(u)}
                      />
                    </SuiBox>
                  );
                })}
              </SuiBox>
            </Card>
          </SuiBox>
          <SuiBox sx={{ maxHeight: 300, overflow: "auto", mt: 2 }}>
            <Comments subjectid={"portfolio_" + detail.id} />
          </SuiBox>
        </SuiBox>
      ) : null}

      {productDetailVisible && selectedProductId ? (
        <ProductDetailModal
          visible={productDetailVisible}
          handleClose={() => setProductDetailVisible(false)}
          productId={selectedProductId}
        />
      ) : null}
    </div>
  );
};

export default CollectionDetail;
